import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Phone, Send, Loader2, Heart, Shield, Eye, Users, MessageCircle, Target } from 'lucide-react';
import { toast } from 'sonner';
import authAxios, { API } from '../utils/authAxios';

const contactOptions = [
  { value: 'consulta_plataforma', label: 'Consulta sobre la plataforma' },
  { value: 'sugerencia', label: 'Sugerencia' },
  { value: 'denuncia', label: 'Denuncia' },
  { value: 'asociaciones', label: 'Asociaciones' },
  { value: 'registrarme', label: 'Quiero registrarme' },
  { value: 'planes-publicidad', label: 'Consulta sobre Planes de Publicidad' },
  { value: 'otros', label: 'Otros' }
];

// Map URL params to form values and default messages
const asuntoMapping = {
  'planes-publicidad': {
    type: 'planes-publicidad',
    message: 'Hola, me gustaría obtener más información sobre los planes de publicidad disponibles.\n\nTipo de servicio (centro/profesional/taller):\n\nQuedo atento a su respuesta.'
  }
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    type: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [isReferralRequest, setIsReferralRequest] = useState(false);

  // Handle URL parameter for pre-filling form
  useEffect(() => {
    const asunto = searchParams.get('asunto');
    if (asunto && asuntoMapping[asunto]) {
      const mapping = asuntoMapping[asunto];
      setFormData(prev => ({
        ...prev,
        type: mapping.type,
        message: mapping.message
      }));
      setIsReferralRequest(asunto.includes('plan-referidos'));
    }
  }, [searchParams]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.type || !formData.message) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    setLoading(true);
    try {
      await authAxios.post(`${API}/contact`, formData);
      // Redirect to thank you page with source parameter
      const source = formData.type.includes('plan-referidos') ? 'plan-referidos' : 'contacto';
      navigate(`/gracias?from=${source}`);
    } catch (error) {
      console.error('Error sending contact:', error);
      toast.error('Error al enviar el mensaje. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Contacto | SinAdicciones.org - Centros de Rehabilitación Chile</title>
        <meta name="description" content="Contáctanos para consultas sobre centros de rehabilitación, planes de publicidad, sugerencias o denuncias. Estamos aquí para ayudarte." />
        <meta name="keywords" content="contacto sinadicciones, ayuda adicciones, centros rehabilitación Chile, consultas" />
        <link rel="canonical" href="https://sinadicciones.org/contacto" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/contacto" />
        <meta property="og:title" content="Contacto — SinAdicciones.org" />
        <meta property="og:description" content="Contáctanos para consultas sobre centros de rehabilitación, planes de publicidad, sugerencias o denuncias." />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contacto — SinAdicciones.org" />
        <meta name="twitter:description" content="Contáctanos para consultas, publicidad o sugerencias." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
      
      <Header />
      
      <main className="pt-20">
        {/* Hero */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Contáctanos
            </h1>
            <p className="text-xl text-slate-400">
              Estamos aquí para ayudarte. Escríbenos y te responderemos lo antes posible.
            </p>
          </div>
        </section>

        {/* Vision Section */}
        <section className="py-12 px-4 bg-slate-900/50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <Eye className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-2">Nuestra Visión</h2>
            </div>
            
            <div className="prose prose-invert max-w-none">
              <p className="text-lg text-slate-300 mb-6">
                En <span className="text-emerald-400 font-semibold">SinAdicciones.org</span> creemos que la recuperación de las adicciones debe ser un <strong className="text-white">derecho humano digno, accesible y libre de abusos</strong>.
              </p>
              
              <p className="text-slate-400 mb-6">
                Nacimos para romper el silencio y la desinformación: miles de personas en Chile buscan ayuda y se encuentran con promesas falsas, centros que humillan en nombre de la "terapia" o simplemente no saben por dónde empezar.
              </p>
              
              <p className="text-lg text-white font-medium mb-4">Nuestra visión es clara y firme:</p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 flex items-start gap-3">
                    <Heart className="w-6 h-6 text-rose-400 flex-shrink-0 mt-1" />
                    <p className="text-slate-300 text-sm">Ser el <strong className="text-white">puente honesto</strong> entre quien necesita ayuda y los centros que realmente sanan.</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 flex items-start gap-3">
                    <Eye className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-1" />
                    <p className="text-slate-300 text-sm">Dar visibilidad a tratamientos <strong className="text-white">reales, éticos y efectivos</strong> (gratuitos y privados).</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 flex items-start gap-3">
                    <Shield className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
                    <p className="text-slate-300 text-sm">Recopilar <strong className="text-white">denuncias anónimas</strong> para exponer malas prácticas y presionar por una rehabilitación regulada.</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4 flex items-start gap-3">
                    <Users className="w-6 h-6 text-sky-400 flex-shrink-0 mt-1" />
                    <p className="text-slate-300 text-sm">Ofrecer <strong className="text-white">herramientas gratuitas</strong> (tests, calculadora, Reto 21 días) para que nadie elija a ciegas.</p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-xl p-6 text-center">
                <p className="text-lg text-slate-300 mb-4">
                  Queremos una <span className="text-emerald-400 font-semibold">sobriedad con sentido</span>: no solo dejar de consumir, sino reconstruir vidas con propósito, dignidad y esperanza.
                </p>
                <p className="text-slate-400 mb-4">
                  Porque nadie debería pagar con su salud o su vida el precio de la desinformación o el maltrato.
                </p>
                <p className="text-xl font-bold text-white">
                  SinAdicciones.org no es un negocio.<br />
                  <span className="text-emerald-400">Es una causa.</span>
                </p>
                <p className="text-slate-400 mt-2">Y tú puedes ser parte de ella.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Información de Contacto</h2>
                
                <div className="space-y-6">
                  <a 
                    href="mailto:contacto@sinadicciones.org"
                    className="flex items-center gap-4 p-4 bg-slate-900 rounded-xl border border-slate-800 hover:border-emerald-500/50 transition-colors group"
                  >
                    <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                      <Mail className="w-6 h-6 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium group-hover:text-emerald-400 transition-colors">Email</p>
                      <p className="text-slate-400 text-sm">contacto@sinadicciones.org</p>
                    </div>
                  </a>
                  
                  <a 
                    href="https://wa.me/56928341147"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-slate-900 rounded-xl border border-slate-800 hover:border-green-500/50 transition-colors group"
                  >
                    <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                      <Phone className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium group-hover:text-green-400 transition-colors">WhatsApp</p>
                      <p className="text-slate-400 text-sm">+56 9 2834 1147 — Equipo SinAdicciones</p>
                    </div>
                  </a>
                  
                  <a 
                    href="https://wa.me/56928341147?text=Hola%2C%20tengo%20una%20consulta"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-slate-900 rounded-xl border border-slate-800 hover:border-sky-500/50 transition-colors group"
                  >
                    <div className="w-12 h-12 rounded-full bg-sky-500/10 flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-sky-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium group-hover:text-sky-400 transition-colors">Chat Directo</p>
                      <p className="text-slate-400 text-sm">Respuesta rápida por WhatsApp</p>
                    </div>
                  </a>
                </div>
                
                <div className="mt-8 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
                  <p className="text-slate-400 text-sm">
                    <strong className="text-white">Horario de atención:</strong><br />
                    Lunes a Viernes: 9:00 - 18:00<br />
                    Sábado: 10:00 - 14:00
                  </p>
                </div>
              </div>
              
              {/* Form */}
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Envíanos un Mensaje</h2>
                
                {/* Referral Request Banner */}
                {false && isReferralRequest && (
                  <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30 mb-6">
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <Target className="w-6 h-6 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-emerald-400">Solicitud recibida</h3>
                        <p className="text-sm text-slate-400">Completa tus datos y te contactaremos a la brevedad.</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="name" className="text-slate-300">Nombre *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Tu nombre completo"
                        className="bg-slate-900 border-slate-700 text-white mt-1"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email" className="text-slate-300">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="tu@email.com"
                        className="bg-slate-900 border-slate-700 text-white mt-1"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="phone" className="text-slate-300">Teléfono</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange('phone', e.target.value)}
                        placeholder="+56 9 1234 5678"
                        className="bg-slate-900 border-slate-700 text-white mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-slate-300">Tipo de consulta *</Label>
                      <Select value={formData.type} onValueChange={(value) => handleChange('type', value)}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white mt-1">
                          <SelectValue placeholder="Selecciona una opción" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {contactOptions.map(option => (
                            <SelectItem 
                              key={option.value} 
                              value={option.value}
                              className="text-white hover:bg-slate-800"
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="message" className="text-slate-300">Mensaje *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        placeholder="Escribe tu mensaje aquí..."
                        className="bg-slate-900 border-slate-700 text-white mt-1 min-h-[120px]"
                        required
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Enviar Mensaje
                        </>
                      )}
                    </Button>
                    
                    <p className="text-xs text-slate-500 text-center">
                      Al enviar este formulario aceptas nuestra{' '}
                      <a href="/privacidad" className="text-emerald-400 hover:underline">política de privacidad</a>.
                    </p>
                  </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
